import type { IntegrationStatus, IntegrationUpdate, RadarMeeting, RadarPerson, UserSummary, WorkspaceRole } from "./model";
import { oauthCompletionUrl } from "./auth";
import { pushDeviceId, type PushSettingsResponse, type PushSubscriptionJSON } from "./push";

export type RadarProjection = { meetings: RadarMeeting[]; people: RadarPerson[] };
export type RadarStateResponse = RadarProjection & { sequence: number };
export class HttpError extends Error {
  constructor(readonly status: number, body: string) {
    super(`${status}:${body}`);
    this.name = "HttpError";
  }
}
export type BootstrapResponse = {
  authenticated: boolean;
  supportedAccount: boolean;
  user: UserSummary | null;
  organization: { id: string; name: string; hostedDomain: string } | null;
  role: WorkspaceRole | null;
  integration: IntegrationStatus | null;
};
export type HealthResponse = {
  ok: boolean;
  build: { gitCommit: string; cloudflareWorkerVersion: string };
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: "include",
    headers: { "content-type": "application/json", ...init?.headers },
    ...init
  });
  const text = await response.text();
  if (!response.ok) throw new HttpError(response.status, text);
  return (text ? JSON.parse(text) : undefined) as T;
}

export const frontendApi = {
  health: () => request<HealthResponse>("/api/health", { cache: "no-store" }),
  bootstrap: () => request<BootstrapResponse>("/api/bootstrap"),
  state: () => request<unknown>("/api/state"),
  refresh: () => request<unknown>("/api/refresh", { method: "POST", body: "{}" }),
  settings: () => request<IntegrationStatus>("/api/settings/sowork"),
  saveSettings: (settings: IntegrationUpdate) => request<IntegrationStatus>("/api/settings/sowork", { method: "PUT", body: JSON.stringify(settings) }),
  pushSettings: async (): Promise<PushSettingsResponse> => {
    const [config, status] = await Promise.all([
      request<{ configured: boolean; publicKey: string | null }>("/api/notifications/config"),
      request<{ subscriptions: Array<{ deviceId: string }> }>("/api/notifications/status")
    ]);
    return {
      vapidPublicKey: config.configured ? config.publicKey ?? "" : "",
      subscribed: status.subscriptions.some((subscription) => subscription.deviceId === pushDeviceId())
    };
  },
  savePushSubscription: (subscription: PushSubscriptionJSON) => request<void>("/api/notifications/subscription", { method: "PUT", body: JSON.stringify({ deviceId: pushDeviceId(), subscription }) }),
  deletePushSubscription: () => request<void>("/api/notifications/subscription", { method: "DELETE", body: JSON.stringify({ deviceId: pushDeviceId() }) }),
  testPushNotification: () => request<void>("/api/notifications/test", { method: "POST", body: "{}" }),
  signIn: () => request<{ url?: string }>("/api/auth/sign-in/social", { method: "POST", body: JSON.stringify({ provider: "google", callbackURL: oauthCompletionUrl(location.origin) }) }),
  signOut: () => request<void>("/api/auth/sign-out", { method: "POST", body: "{}" })
};

export function meetingsFromState(payload: unknown): RadarMeeting[] {
  const raw = (payload as { meetings?: Array<Record<string, unknown>> })?.meetings ?? [];
  return raw.flatMap((meeting): RadarMeeting[] => {
    const active = meeting.status === "active";
    if (active && meeting.visible === false) return [];
    const current = (meeting.currentParticipants as Array<{ user?: { name?: string; id?: string; avatarUrl?: string } }>) ?? [];
    const all = (meeting.allParticipants as Array<{ user?: { name?: string; id?: string; avatarUrl?: string } }>) ?? current;
    const selected = active ? current : all;
    if (selected.length === 0) return [];
    const participants = selected.map(({ user }) => user?.name ?? user?.id ?? "Unknown");
    return [{
      id: String(meeting.id), room: String(meeting.topic ?? "Meeting"), participants,
      participantIds: selected.map(({ user }) => user?.id ?? ""),
      participantAvatarUrls: selected.map(({ user }) => typeof user?.avatarUrl === "string" ? user.avatarUrl : undefined),
      startedAt: new Date(Number(meeting.startedAt) * 1000),
      endedAt: meeting.endedAt ? new Date(Number(meeting.endedAt) * 1000) : undefined,
      status: active ? "live" : "ended"
    }];
  });
}

export function peopleFromState(payload: unknown): RadarPerson[] {
  const raw = (payload as { people?: Array<Record<string, unknown>> })?.people ?? [];
  return raw.flatMap((person): RadarPerson[] => {
    if (typeof person.id !== "string" || typeof person.availability !== "string") return [];
    return [{
      id: person.id,
      name: typeof person.name === "string" ? person.name : undefined,
      avatarUrl: typeof person.avatarUrl === "string" ? person.avatarUrl : undefined,
      availability: person.availability,
      timestamp: typeof person.observedAt === "number"
        ? person.observedAt
        : typeof person.timestamp === "number" ? person.timestamp : 0
    }];
  });
}

export function radarFromState(payload: unknown): RadarProjection {
  return { meetings: meetingsFromState(payload), people: peopleFromState(payload) };
}

export function parseRadarState(payload: unknown): RadarStateResponse | null {
  if (typeof payload !== "object" || payload === null) return null;
  const raw = payload as Record<string, unknown>;
  if (!Number.isSafeInteger(raw.sequence) || (raw.sequence as number) < 0) return null;
  if (!Array.isArray(raw.meetings) || !Array.isArray(raw.people)) return null;
  return { ...radarFromState(raw), sequence: raw.sequence as number };
}

export function isAuthExpired(error: unknown): boolean {
  return error instanceof HttpError && (error.status === 401 || error.status === 403);
}

export function createRadarEventsSocket(): WebSocket {
  const protocol = location.protocol === "https:" ? "wss:" : "ws:";
  return new WebSocket(`${protocol}//${location.host}/api/events`);
}
